import { Fragment, useEffect, useMemo } from 'react';
import { Animated, View } from 'react-native';
import { useSelector } from 'react-redux';
import { Icon, Text, useTheme } from '@ui-kitten/components';

import DateHelper from '../../Helper/Date';
import useMealPlan from '../../Hooks/useMealPlan';
import {
  selectMealPlanCounter,
  selectMealPlanHint,
} from '../../RTK/mealPlan/selectors';
import {
  ConditionalMealPlanHint,
  HintKey,
} from '../../Screens/Store/MealPlanHint';
import themeStyle from '../../Theme/styles';

import Button from '../Button';
import Breadcrumbs from '../Sheets/MealPlanList/Breadcrumbs';
import useMealPlanNavigation from '../Sheets/MealPlanList/controller/useMealPlanNavigation';

import useController from './controller';
import styles from './styles';

function ShopMealPlanCart() {
  const theme = useTheme();
  const showFloatingButtonHintSelector = useMemo(
    () => selectMealPlanHint('showFloatingButtonHint'),
    [],
  );
  const showFloatingButtonHint = useSelector(showFloatingButtonHintSelector);
  const { shakeAnimation, animateButton } = useController();
  const { mealPlanDay } = useMealPlan();
  const { days, isFirstDay, onBreadCrumbsPress, onPrev, onNext } =
    useMealPlanNavigation(true);
  const cartCounter = useSelector((state) =>
    selectMealPlanCounter(state, mealPlanDay?.value),
  );

  // effect for: every time meal plan total quantity changed, animate the button
  useEffect(() => {
    if (cartCounter.current !== 0) {
      animateButton();
    }
  }, [cartCounter, animateButton]);

  return (
    <Fragment>
      <View style={styles.mealplanNavigationWrapper}>
        <Breadcrumbs
          data={days}
          value={mealPlanDay?.value}
          onClick={onBreadCrumbsPress}
        />
      </View>
      <View style={themeStyle.flexDirectionRowCenterCenter}>
        {mealPlanDay?.value > 1 && (
          <ConditionalMealPlanHint
            enabled={showFloatingButtonHint}
            hintKey={HintKey.PREV}
          >
            <Button
              status="control"
              style={styles.btnCtrl}
              accessoryLeft={
                <Icon
                  name="chevron-left"
                  fill={theme.icon_color || theme['color-success-500']}
                  style={styles.icon}
                />
              }
              onPress={onPrev}
            />
          </ConditionalMealPlanHint>
        )}

        <View
          style={[
            themeStyle.flex1,
            mealPlanDay?.value > 1
              ? themeStyle.pageHorizontalSpacingSmall
              : themeStyle.spacingRightSmall,
          ]}
        >
          <ConditionalMealPlanHint
            enabled={showFloatingButtonHint}
            hintKey={HintKey.REVIEW}
          >
            <Animated.View
              style={[
                styles.wrapper,
                themeStyle.pageVerticalSpacingMedium,
                { transform: [{ translateX: shakeAnimation }] },
                theme.button_color && { backgroundColor: theme.button_color },
              ]}
            >
              <View style={themeStyle.flexDirectionRowCenterCenter}>
                <Text appearance="alternative" numberOfLines={1}>
                  {DateHelper.convertNumberToDay(mealPlanDay?.value).substring(
                    0,
                    3,
                  )}
                </Text>

                <View style={styles.textContainer}>
                  <Text appearance="alternative" numberOfLines={1}>
                    Review order
                  </Text>
                </View>

                <Text
                  appearance="alternative"
                  style={styles.qty}
                  numberOfLines={1}
                >
                  {isFirstDay || cartCounter.current === cartCounter.monday
                    ? `${cartCounter.current}×`
                    : `${cartCounter.current}/${cartCounter.monday}`}
                </Text>
              </View>
            </Animated.View>
          </ConditionalMealPlanHint>
        </View>

        {mealPlanDay?.value < 5 && (
          <ConditionalMealPlanHint
            enabled={showFloatingButtonHint}
            hintKey={HintKey.NEXT}
          >
            <Button
              status="control"
              style={styles.btnCtrl}
              accessoryLeft={
                <Icon
                  name="chevron-right"
                  fill={theme.icon_color || theme['color-success-500']}
                  style={styles.icon}
                />
              }
              onPress={onNext}
            />
          </ConditionalMealPlanHint>
        )}
      </View>
    </Fragment>
  );
}

export default ShopMealPlanCart;
